import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { Loader2, MapPin } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const ACCRA = [5.6037, -0.187];

/** Drag the pin to put a branch exactly where customers will walk in. */
export const BranchPinDialog = ({ branch, onClose, onSaved }) => {
  const holder = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [coords, setCoords] = useState(
    branch?.latitude && branch?.longitude ? [branch.latitude, branch.longitude] : null,
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!holder.current || map.current) return;
    const start = coords || ACCRA;
    map.current = L.map(holder.current).setView(start, coords ? 16 : 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    }).addTo(map.current);
    marker.current = L.marker(start, { draggable: true }).addTo(map.current);
    marker.current.on('dragend', () => {
      const { lat, lng } = marker.current.getLatLng();
      setCoords([Number(lat.toFixed(6)), Number(lng.toFixed(6))]);
    });
    map.current.on('click', (e) => {
      marker.current.setLatLng(e.latlng);
      setCoords([Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))]);
    });
    setTimeout(() => map.current?.invalidateSize(), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!coords) return;
    setBusy(true);
    try {
      await api.put(`/branches/${branch.tenant_id}`, { latitude: coords[0], longitude: coords[1] });
      toast.success('Map pin saved');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the pin'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="branch-pin-dialog">
        <DialogHeader>
          <DialogTitle>Where is {branch?.name}?</DialogTitle>
          <DialogDescription>
            We placed the pin from the address. Drag it or tap the map to correct it — this is what
            customers see on the Find a pharmacy map.
          </DialogDescription>
        </DialogHeader>
        <div ref={holder} className="h-72 w-full rounded-lg border overflow-hidden" data-testid="branch-pin-map" />
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="branch-pin-coords">
          <MapPin className="h-3.5 w-3.5" />
          {coords ? `${coords[0]}, ${coords[1]}` : 'No pin yet — tap the map to place one'}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !coords} data-testid="branch-pin-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save pin
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
